import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Badge,
  Container,
  Row,
  Col,
  Tabs,
  Tab,
  Modal,
  Form,
  Alert,
  Spinner,
} from '@themesberg/react-bootstrap';
import {
  CheckCircle2,
  PackageSearch,
  PackagePlus,
  Truck,
  MapPin,
  Box as BoxIcon,
  ShoppingBasket,
  ShoppingCart,
  Calendar,
  HeadphonesIcon,
  Pin,
} from 'lucide-react';
import { apiCall } from '../../../../middlewares/api';
import { apiCallConsts } from '../../../../constants/apiCallConsts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

// =========== DUMMY API CALLS ===========
const fetchOrderData = (id, source, mobileNumber) =>
  new Promise(async (resolve, reject) => {
    try {
      const data = await axios.get(
        `https://new-oms-server-prod.fly.dev/v1/tracking/trackingDetails/${id}`,
        {
          params: { source, mobileNumber },
        }
      );
      // If the server  responds with an error or session expired, we can reject:

      resolve(data?.data);
    } catch (error) {
      reject(error);
    }
  });

const fetchPopularPicks = () =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve([
        {
          id: 1,
          name: 'Apple Watch Series 8',
          price: '₹34,999',
          image:
            'https://vsprod.vijaysales.com/media/catalog/product/2/3/232341-1_1_.jpg?optimize=medium&fit=bounds&height=500&width=500',
          url: 'https://www.vijaysales.com/p/206493/store-display-unit-apple-watch-series-8-45-mm-midnight-aluminum-case-with-midnight-sport-band-gps',
        },
        {
          id: 2,
          name: 'Apple iPad Pro 11-inch',
          price: '₹81,999',
          image:
            'https://vsprod.vijaysales.com/media/catalog/product/2/1/211864-image1_2.jpg?optimize=medium&fit=bounds&height=500&width=500',
          url: 'https://www.vijaysales.com/p/229459/apple-ipad-pro-2024-256gb-wifi-11-inch-28-22-cm-standard-glass-ultra-retina-xdr-display-space-black-mvv83hn-a',
        },
        {
          id: 3,
          name: 'Apple Pencil 2',
          price: '₹9,999',
          image:
            'https://vsprod.vijaysales.com/media/catalog/product/2/2/229621-image1.jpg?optimize=medium&fit=bounds&height=500&width=500',
          url: 'https://www.vijaysales.com/p/222615/apple-pencil-usb-c',
        },
        {
          id: 4,
          name: 'Apple AirPods Pro',
          price: '₹14,999',
          image:
            'https://vsprod.vijaysales.com/media/catalog/product/2/2/221167-image1.png?optimize=medium&fit=bounds&height=500&width=500&format=jpeg',
          url: 'https://www.vijaysales.com/p/221167/apple-airpods-pro-2nd-generation-with-magsafe-charging-case-usb-c',
        },
      ]);
    }, 1000);
  });

// =========== STATUS ICONS MAPPING ===========
const statusIcons = {
  'ORDER PLACED': {
    icon: <CheckCircle2 color="#4caf50" size={20} />,
    color: '#4caf50',
    style: { color: '#4caf50' },
  },
  'ORDER APPROVED': {
    icon: <PackageSearch color="#0288d1" size={20} />,
    color: '#0288d1',
    style: { color: '#0288d1' },
  },
  'PENDING PICKUP': {
    icon: <PackagePlus color="#ff9800" size={20} />,
    color: '#ff9800',
    style: { color: '#ff9800' },
  },
  'PICKUP DONE': {
    icon: <Truck color="#2c90ff" size={20} />,
    color: '#2c90ff',
    style: { color: '#2c90ff' },
  },
  'IN-TRANSIT': {
    icon: <BoxIcon color="#ffc107" size={20} />,
    color: '#ffc107',
    style: { color: '#ffc107' },
  },
  'OUT FOR DELIVERY': {
    icon: <MapPin color="#673ab7" size={20} />,
    color: '#673ab7',
    style: { color: '#673ab7' },
  },
  DELIVERED: {
    icon: <CheckCircle2 color="#4caf50" size={20} />,
    color: '#4caf50',
    style: { color: '#4caf50' },
  },
};

// =========== CUSTOM HOOK: DETECT MOBILE VIEW ===========
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

// =========== GLOBAL SHIMMER STYLES FOR LOADER ===========
const loaderGlobalStyles = `
  .skeleton {
    background-color: #e0e0e0;
    position: relative;
    overflow: hidden;
  }
  .skeleton::after {
    content: '';
    position: absolute;
    top: 0;
    left: -150px;
    width: 150px;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
    animation: shimmer 1.5s infinite;
  }
  @keyframes shimmer {
    0% { left: -150px; }
    100% { left: 100%; }
  }
`;

// =========== SHIMMER/LOADER COMPONENT ===========
const Loader = ({ isMobile }) => {
  const HeaderSkeleton = () => {
    if (isMobile) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#F5F8FB',
            padding: '15px 10px',
            marginBottom: '15px',
          }}
        >
          <div
            className="skeleton"
            style={{ width: '100%', height: '30px', marginBottom: '10px' }}
          />
          <div
            style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}
          >
            <div
              className="skeleton"
              style={{ width: '130px', height: '35px' }}
            />
            <div
              className="skeleton"
              style={{ width: '130px', height: '35px' }}
            />
          </div>
        </div>
      );
    }
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#F5F8FB',
          padding: '15px 10px',
          marginBottom: '15px',
        }}
      >
        <div className="skeleton" style={{ width: '40%', height: '30px' }} />
        <div style={{ display: 'flex', gap: '10px' }}>
          <div
            className="skeleton"
            style={{ width: '130px', height: '35px' }}
          />
          <div
            className="skeleton"
            style={{ width: '130px', height: '35px' }}
          />
        </div>
      </div>
    );
  };

  const OrderSummarySkeleton = () => {
    const summaryItemStyle = isMobile
      ? { display: 'flex', alignItems: 'center', marginBottom: '10px' }
      : { flex: '1 1 18%', marginBottom: '10px', minWidth: '150px' };

    const labelStyle = isMobile
      ? {
          color: '#9e9e9e',
          fontSize: '13px',
          width: '100px',
          marginRight: '10px',
        }
      : { color: '#9e9e9e', fontSize: '13px', marginBottom: '4px' };

    const valueStyle = { color: '#212121', fontWeight: 500 };

    return (
      <div
        style={{
          display: isMobile ? 'block' : 'flex',
          flexWrap: 'wrap',
          border: '1px solid #E0E0E0',
          backgroundColor: '#fff',
          padding: '15px',
          marginBottom: '20px',
        }}
      >
        <div style={summaryItemStyle}>
          <div style={labelStyle}>AWB No.</div>
          <div style={valueStyle}>{/* Value will be inserted later */}</div>
        </div>
        <div style={summaryItemStyle}>
          <div style={labelStyle}>Order Status</div>
          <Badge
            style={{
              fontSize: '12px',
              backgroundColor: '#d2f4dd',
              color: '#2e7d32',
              padding: '4px 10px',
              fontWeight: 600,
            }}
          >
            {/* Order Status Value */}
          </Badge>
        </div>
        <div style={summaryItemStyle}>
          <div style={labelStyle}>Expected Delivery Date</div>
          <div style={valueStyle}></div>
        </div>
        <div style={summaryItemStyle}>
          <div style={labelStyle}>Delivery Partner</div>
          <div
            style={{ ...valueStyle, color: '#2c90ff', fontWeight: 700 }}
          ></div>
        </div>
        <div style={summaryItemStyle}>
          <div style={labelStyle}>Payment Status & Mode</div>
          <div style={valueStyle}></div>
        </div>
      </div>
    );
  };

  const ProductInfoSkeleton = () => (
    <div
      style={{
        border: '1px solid #ddd',
        backgroundColor: '#fff',
        marginBottom: '15px',
        padding: '15px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '12px',
        }}
      >
        <div className="skeleton" style={{ width: '100px', height: '18px' }} />
        <div className="skeleton" style={{ width: '20px', height: '18px' }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div
          className="skeleton"
          style={{ width: '60px', height: '60px', marginRight: '10px' }}
        />
        <div style={{ flex: 1 }}>
          <div
            className="skeleton"
            style={{ width: '60%', height: '16px', marginBottom: '6px' }}
          />
          <div
            className="skeleton"
            style={{ width: '40%', height: '14px', marginBottom: '6px' }}
          />
          <div className="skeleton" style={{ width: '30%', height: '16px' }} />
        </div>
      </div>
    </div>
  );

  const TrackingHistorySkeleton = () => (
    <div
      style={{
        border: '1px solid #ddd',
        backgroundColor: '#fff',
        marginBottom: '15px',
        padding: '15px',
      }}
    >
      <div
        className="skeleton"
        style={{ width: '120px', height: '18px', marginBottom: '15px' }}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '100px 40px auto' : '140px 60px auto',
          rowGap: '22px',
          position: 'relative',
          alignItems: 'flex-start',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '45px',
            bottom: '20px',
            left: isMobile ? '115px' : '165px',
            width: '2px',
            background: '#ddd',
          }}
        />
        {Array.from({ length: 4 }).map((_, index) => (
          <React.Fragment key={index}>
            <div style={{ textAlign: 'right' }}>
              <div
                className="skeleton"
                style={{ width: '80%', height: '14px', margin: '0 auto' }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div
                className="skeleton"
                style={{ width: '40px', height: '40px', borderRadius: '50%' }}
              />
            </div>
            <div>
              <div
                className="skeleton"
                style={{ width: '50%', height: '14px', marginBottom: '6px' }}
              />
              <div
                className="skeleton"
                style={{ width: '70%', height: '14px', marginBottom: '6px' }}
              />
              <div
                className="skeleton"
                style={{ width: '40%', height: '14px' }}
              />
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const CustomerInfoSkeleton = () => (
    <div
      style={{
        border: '1px solid #ddd',
        backgroundColor: '#fff',
        marginBottom: '15px',
        padding: '15px',
      }}
    >
      <div
        className="skeleton"
        style={{ width: '150px', height: '18px', marginBottom: '10px' }}
      />
      {Array.from({ length: 4 }).map((_, idx) => (
        <div
          key={idx}
          className="skeleton"
          style={{ width: '80%', height: '14px', marginBottom: '6px' }}
        />
      ))}
    </div>
  );

  const DeliveryLocationSkeleton = () => (
    <div
      style={{
        border: '1px solid #ddd',
        backgroundColor: '#fff',
        marginBottom: '15px',
      }}
    >
      <div
        style={{
          padding: '10px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <div className="skeleton" style={{ width: '160px', height: '16px' }} />
        <div className="skeleton" style={{ width: '120px', height: '30px' }} />
      </div>
      <div className="skeleton" style={{ width: '100%', height: '300px' }} />
    </div>
  );

  const PopularPicksSkeleton = () => (
    <div
      style={{ backgroundColor: '#F5F8FB', padding: '15px', marginTop: '15px' }}
    >
      <div
        className="skeleton"
        style={{ width: '140px', height: '18px', marginBottom: '15px' }}
      />
      <div className="row" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
        {Array.from({ length: 4 }).map((_, idx) => (
          <div
            key={idx}
            className="d-inline-block"
            style={{
              width: '200px',
              marginRight: '10px',
              verticalAlign: 'top',
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'row',
                height: '150px',
              }}
            >
              <div
                className="skeleton"
                style={{ width: '40%', margin: 'auto 10px', height: '80%' }}
              />
              <div
                style={{
                  flex: 1,
                  padding: '10px 0',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div
                    className="skeleton"
                    style={{
                      width: '70%',
                      height: '14px',
                      marginBottom: '6px',
                    }}
                  />
                  <div
                    className="skeleton"
                    style={{
                      width: '50%',
                      height: '14px',
                      marginBottom: '6px',
                    }}
                  />
                </div>
                <div
                  className="skeleton"
                  style={{ width: '60%', height: '30px' }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ padding: '15px', backgroundColor: '#F5F8FB' }}>
        <style>{loaderGlobalStyles}</style>
        <HeaderSkeleton />
        <OrderSummarySkeleton />
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #ddd',
            marginBottom: '15px',
          }}
        >
          <div
            className="skeleton"
            style={{ width: '100px', height: '30px', marginRight: '8px' }}
          />
          <div
            className="skeleton"
            style={{ width: '120px', height: '30px' }}
          />
        </div>
        <ProductInfoSkeleton />
        <TrackingHistorySkeleton />
        <CustomerInfoSkeleton />
        <DeliveryLocationSkeleton />
        <PopularPicksSkeleton />
      </div>
    );
  }
  return (
    <div style={{ padding: '15px', backgroundColor: '#F5F8FB' }}>
      <style>{loaderGlobalStyles}</style>
      <HeaderSkeleton />
      <OrderSummarySkeleton />
      <div className="row">
        <div className="col-md-6">
          <CustomerInfoSkeleton />
          <DeliveryLocationSkeleton />
        </div>
        <div className="col-md-6">
          <ProductInfoSkeleton />
          <TrackingHistorySkeleton />
        </div>
      </div>
      <PopularPicksSkeleton />
    </div>
  );
};

// =========== MAIN ORDER TRACKING COMPONENT ===========
const OrderTracking = () => {
  const isMobile = useIsMobile();
  const [orderData, setOrderData] = useState(null);
  const [popularPicks, setPopularPicks] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [searchParams] = useSearchParams();
  console.log(searchParams.keys().toArray());
  const [error, setError] = useState(null);
  const [showPanModal, setShowPanModal] = useState(false);
  const [panInput, setPanInput] = useState('');
  const [panSubmitting, setPanSubmitting] = useState(false);
  const [panError, setPanError] = useState('');
  const [panSuccess, setPanSuccess] = useState('');
  //?id=68be9f9b7962d405a159922c&source=dms&mobileNumber=8424002308
  const id = searchParams.get('id');
  const source = searchParams.get('source');
  const mobileNumber = searchParams.get('mobileNumber');
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [order, picks] = await Promise.all([
          fetchOrderData(id, source, mobileNumber),
          fetchPopularPicks(),
        ]);
        setOrderData(order);
        setPopularPicks(picks);
      } catch (err) {
        setError(err?.toString() || 'Something went wrong');
      }
    })();
  }, [id]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => console.error('Geolocation error:', error)
      );
    }
  }, []);

  if (error) {
    return (
      <Container
        fluid
        style={{
          padding: '15px',
          backgroundColor: '#F5F8FB',
          minHeight: '100vh',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            marginTop: '50px',
            backgroundColor: '#fff',
            padding: '30px',
            border: '1px solid #ddd',
            borderRadius: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            maxWidth: '400px',
            margin: '50px auto',
          }}
        >
          <h5 style={{ fontWeight: 'bold', marginBottom: '20px' }}>
            !oops your session has expired
          </h5>
          <p style={{ fontSize: '14px', marginBottom: '20px' }}>
            Please go back to your orders.
          </p>
          <Button
            style={{
              fontSize: '14px',
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              borderRadius: '0',
              padding: '8px 16px',
              color: 'black',
            }}
          >
            <a
              href={process.env.REACT_APP_WEBSITE_URL}
              style={{ color: 'black', textDecoration: 'none' }}
            >
              Go back to My Orders
            </a>
          </Button>
        </div>
      </Container>
    );
  }

  if (!orderData) {
    return <Loader isMobile={isMobile} />;
  }

  const {
    orderId,
    awbNo,
    orderStatus,
    expectedDeliveryDate,
    deliveryPartner,
    paymentMode,
    customer,
    product,
    trackingHistory,
    shippingLocation,
  } = orderData;

  const panNumber =
    orderData?.panNumber ||
    orderData?.panNo ||
    orderData?.pan ||
    customer?.pan ||
    customer?.panNumber ||
    '';
  const orderNumber = orderData?.orderNo || orderId || id;
  const isPanRequired = Boolean(
    orderData?.isPanRequired === true ||
    orderData?.isPanRequired === 'true' ||
    searchParams.get('isPanRequired') === 'true'
  );

  const handleOpenPanModal = () => {
    setPanInput(panNumber || '');
    setPanError('');
    setPanSuccess('');
    setShowPanModal(true);
  };

  const handlePanSubmit = async (e) => {
    if (e) e.preventDefault();
    const cleanPan = (panInput || '').trim().toUpperCase();
    if (!cleanPan) {
      setPanError('PAN number is required');
      return;
    }
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(cleanPan)) {
      setPanError('Please enter a valid 10-character PAN (e.g. ABCDE1234F)');
      return;
    }

    setPanSubmitting(true);
    setPanError('');
    setPanSuccess('');

    try {
      const url =
        process.env.REACT_APP_ORDER_PAN_URL ||
        (process.env.REACT_APP_NEW_OMS_URL
          ? `${process.env.REACT_APP_NEW_OMS_URL}/v1/order/pan`
          : typeof window !== 'undefined' &&
            (window.location.hostname === 'localhost' ||
              window.location.hostname === '127.0.0.1')
          ? 'http://localhost:3001/v1/order/pan'
          : 'https://new-oms-server-prod.fly.dev/v1/order/pan');

      const payload = {
        orderNo: orderNumber,
        panNumber: cleanPan,
      };

      await axios.put(url, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      setOrderData((prev) => ({
        ...prev,
        panNumber: cleanPan,
        panNo: cleanPan,
        customer: {
          ...prev?.customer,
          pan: cleanPan,
          panNumber: cleanPan,
        },
      }));

      setPanSuccess('PAN number updated successfully!');
      setTimeout(() => {
        setShowPanModal(false);
        setPanSuccess('');
      }, 1200);
    } catch (err) {
      console.error('Failed to update PAN:', err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to update PAN. Please try again.';
      setPanError(errorMsg);
    } finally {
      setPanSubmitting(false);
    }
  };

  const OrderHeader = () => {
    if (isMobile) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#F5F8FB',
            padding: '15px 10px',
            marginBottom: '10px',
          }}
        >
          <h5 style={{ fontWeight: 'bold', margin: 0 }}>Order ID {orderId}</h5>
          {isPanRequired && (
            <Button
              style={{
                fontSize: '12px',
                backgroundColor: '#fff',
                border: '1px solid #0d6efd',
                borderRadius: '4px',
                padding: '4px 10px',
                color: '#0d6efd',
                fontWeight: 600,
              }}
              onClick={handleOpenPanModal}
            >
              {panNumber ? 'Edit PAN' : '+ Add PAN'}
            </Button>
          )}
        </div>
      );
    }
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#F5F8FB',
          padding: '15px 10px',
          marginBottom: '10px',
        }}
      >
        <h5 style={{ fontWeight: 'bold', margin: 0 }}>Order ID {orderId}</h5>
        <div style={{ display: 'flex', gap: '10px' }}>
          {isPanRequired && (
            <Button
              style={{
                fontSize: '13px',
                backgroundColor: '#fff',
                border: '1px solid #0d6efd',
                borderRadius: '4px',
                padding: '6px 14px',
                color: '#0d6efd',
                fontWeight: 600,
              }}
              onClick={handleOpenPanModal}
            >
              {panNumber ? 'Edit PAN' : '+ Add PAN'}
            </Button>
          )}
        </div>
      </div>
    );
  };

  const OrderSummary = () => {
    const summaryItemStyle = isMobile
      ? { display: 'flex', alignItems: 'center', marginBottom: '10px' }
      : { flex: '1 1 18%', marginBottom: '10px', minWidth: '150px' };

    const labelStyle = isMobile
      ? {
          color: '#9e9e9e',
          fontSize: '13px',
          width: '100px',
          marginRight: '10px',
        }
      : { color: '#9e9e9e', fontSize: '13px', marginBottom: '4px' };

    const valueStyle = { color: '#212121', fontWeight: 500 };

    return (
      <div
        style={{
          display: isMobile ? 'block' : 'flex',
          flexWrap: 'wrap',
          border: '1px solid #E0E0E0',
          backgroundColor: '#fff',
          padding: '15px',
          marginBottom: '20px',
        }}
      >
        {awbNo && (
          <div style={summaryItemStyle}>
            <div style={labelStyle}>AWB No.</div>
            <div style={valueStyle}>{awbNo || '—'}</div>
          </div>
        )}
        <div style={summaryItemStyle}>
          <div style={labelStyle}>Order Status</div>
          <Badge
            style={{
              fontSize: '12px',
              backgroundColor: '#d2f4dd',
              borderRadius: '0px',
              padding: '5px',
              color: '#2e7d32',
              fontWeight: 600,
            }}
          >
            {orderStatus || '—'}
          </Badge>
        </div>
        <div style={summaryItemStyle}>
          <div style={labelStyle}>Expected Delivery Date</div>
          <div style={valueStyle}>{expectedDeliveryDate || '—'}</div>
        </div>
        {deliveryPartner && deliveryPartner !== 'DMS Delivery' && (
          <div style={summaryItemStyle}>
            <div style={labelStyle}>Delivery Partner</div>
            {/* <div style={{ ...valueStyle, color: "#2c90ff", fontWeight: 700 }}>
            {deliveryPartner || "—"}
          </div> */}
            <img
              src={deliveryPartner}
              style={{
                width: '250px',
                height: '30px',
                objectFit: 'contain',
                borderRadius: 0,
              }}
            />
          </div>
        )}
        {paymentMode && (
          <div style={summaryItemStyle}>
            <div style={labelStyle}>Payment Status & Mode</div>
            <div style={valueStyle}>{paymentMode || '—'}</div>
          </div>
        )}
        {isPanRequired && (
          <div style={summaryItemStyle}>
            <div style={labelStyle}>PAN No.</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {panNumber ? (
                <>
                  <span style={valueStyle}>{panNumber}</span>
                  <Button
                    variant="link"
                    size="sm"
                    style={{
                      padding: '0 4px',
                      fontSize: '12px',
                      color: '#0d6efd',
                      textDecoration: 'none',
                      fontWeight: 600,
                    }}
                    onClick={handleOpenPanModal}
                  >
                    Edit
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline-primary"
                  size="sm"
                  style={{
                    fontSize: '12px',
                    padding: '2px 8px',
                    borderRadius: '3px',
                    fontWeight: 600,
                  }}
                  onClick={handleOpenPanModal}
                >
                  + Add PAN
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const CustomerInfo = () => {
    const customerName = customer?.name || 'N/A';
    const customerPhone = customer?.phone || 'N/A';
    const customerEmail = customer?.email || 'N/A';
    const customerAddress = customer?.address || 'N/A';
    const dropPinCode = customer?.dropPinCode || 'N/A';

    return (
      <Card
        style={{
          padding: '15px',
          marginBottom: '15px',
          border: '1px solid #ddd',
          borderRadius: '0',
          backgroundColor: '#fff',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        }}
      >
        <h6 style={{ fontWeight: 'bold', marginBottom: '10px' }}>
          Customer Information
        </h6>
        <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>
          <strong>Name:</strong> {customerName}
        </p>
        <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>
          <strong>Phone:</strong> {customerPhone}
        </p>
        <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>
          <strong>Email:</strong> {customerEmail}
        </p>
        <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>
          <strong>Address:</strong> {customerAddress}
        </p>
        <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>
          <strong>Drop Pin Code:</strong> {dropPinCode}
        </p>
        {isPanRequired && (
          <p style={{ margin: 0, fontSize: '14px' }}>
            <strong>PAN No:</strong>{' '}
            {panNumber ? (
              <span>
                {panNumber}{' '}
                <Button
                  variant="link"
                  size="sm"
                  style={{
                    padding: '0 4px',
                    fontSize: '12px',
                    color: '#0d6efd',
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                  onClick={handleOpenPanModal}
                >
                  Edit
                </Button>
              </span>
            ) : (
              <Button
                variant="link"
                size="sm"
                style={{
                  padding: 0,
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#0d6efd',
                  textDecoration: 'none',
                }}
                onClick={handleOpenPanModal}
              >
                + Add PAN
              </Button>
            )}
          </p>
        )}
      </Card>
    );
  };

  const ProductInfo = () => {
    const productName = product?.name || 'Product Name Unavailable';
    const productPrice = product?.price
      ? `₹${product.price.toLocaleString('en-IN')}`
      : 'Price Unavailable';
    const productQuantity = product?.quantity || 0;
    const productImage = product?.image || 'https://via.placeholder.com/80';

    return (
      <Card
        style={{
          padding: '15px',
          marginBottom: '15px',
          border: '1px solid #ddd',
          borderRadius: '0',
          backgroundColor: '#fff',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
          }}
        >
          <h6 style={{ margin: 0, fontWeight: 'bold', fontSize: '14px' }}>
            Order Details
          </h6>
          <span
            style={{
              color: '#000',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
            }}
          >
            &rsaquo;
          </span>
        </div>
        <Row style={{ alignItems: 'center' }}>
          <Col xs={3} sm={2} md={1} style={{ textAlign: 'center' }}>
            <img
              src={productImage}
              alt="Product"
              style={{ width: '60px', height: 'auto', borderRadius: 0 }}
            />
          </Col>
          <Col xs={5} sm={6} md={7}>
            <p
              style={{
                margin: 0,
                fontWeight: 'bold',
                fontSize: '14px',
                lineHeight: 1.2,
              }}
            >
              {productName}
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: '#777' }}>
              Quantity: {productQuantity}
            </p>
          </Col>
          <Col xs={4} sm={4} md={4} style={{ textAlign: 'right' }}>
            <p
              style={{
                margin: 0,
                fontWeight: 'bold',
                fontSize: '14px',
                color: '#000',
              }}
            >
              {productPrice}
            </p>
          </Col>
        </Row>
      </Card>
    );
  };

  const TrackingHistory = () => {
    const [expanded, setExpanded] = useState(false);
    const [localMobile, setLocalMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
      const handleResize = () => setLocalMobile(window.innerWidth < 768);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    const displayedData = expanded
      ? trackingHistory
      : trackingHistory.slice(-6);
    const gridTemplateColumns = localMobile
      ? '100px 40px auto'
      : '140px 60px auto';
    const verticalLineLeft = localMobile ? '115px' : '165px';

    return (
      <Card
        style={{
          padding: '15px',
          marginBottom: '15px',
          border: '1px solid #ddd',
          backgroundColor: '#fff',
          borderRadius: '0',
          boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
          position: 'relative',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
          }}
        >
          <h6
            style={{
              fontWeight: 'bold',
              margin: 0,
              fontSize: '16px',
              color: '#333',
            }}
          >
            Tracking History
          </h6>
          {trackingHistory.length > 6 && (
            <Button
              onClick={() => setExpanded(!expanded)}
              style={{
                fontSize: '11px',
                background: 'transparent',
                border: 'none',
                color: '#2c90ff',
                padding: '0 5px',
                cursor: 'pointer',
                transition: 'color 0.2s ease',
              }}
              onMouseEnter={(e) => (e.target.style.color = '#1a78d7')}
              onMouseLeave={(e) => (e.target.style.color = '#2c90ff')}
            >
              {expanded ? 'Show Less ▲' : 'Show More ▼'}
            </Button>
          )}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: gridTemplateColumns,
            rowGap: '22px',
            position: 'relative',
            alignItems: 'flex-start',
            marginTop: '10px',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '45px',
              bottom: '20px',
              left: verticalLineLeft,
              width: '2px',
              background: '#ddd',
              zIndex: 0,
            }}
          ></div>
          {displayedData.map((entry, index) => {
            const iconData = statusIcons[entry.status] || {
              icon: <BoxIcon color="#ffc107" size={20} />,
            };
            return (
              <React.Fragment key={index}>
                <div
                  style={{
                    textAlign: 'right',
                    color: '#666',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {new Date(entry.updated_at).toLocaleString('en-US', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                  }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      background: '#f0f8ff',
                      borderRadius: '50%',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {iconData.icon}
                  </div>
                </div>
                <div
                  style={{ fontSize: '13px', color: '#333', lineHeight: '1.4' }}
                >
                  <p style={{ margin: '0 0 3px 0', fontWeight: 'bold' }}>
                    {entry.status}
                  </p>
                  {entry.remarks && (
                    <p style={{ margin: '0 0 3px 0', color: '#777' }}>
                      {entry.remarks}
                    </p>
                  )}
                  {entry.location && (
                    <p
                      style={{
                        margin: 0,
                        color: '#777',
                        fontStyle: 'italic',
                        fontSize: '12px',
                      }}
                    >
                      Location: {entry.location}
                    </p>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </Card>
    );
  };

  const DeliveryLocation = () => {
    const shippingLat = shippingLocation?.latitude || shippingLocation?.lat;
    const shippingLng = shippingLocation?.longitude || shippingLocation?.lng;
    const shippingPincode =
      shippingLocation?.shippingPincode || customer?.dropPinCode;
    const mapQuery =
      shippingLat && shippingLng
        ? `${shippingLat},${shippingLng}`
        : shippingPincode
          ? `${shippingPincode}, India`
          : 'India';
    const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&center=${encodeURIComponent(mapQuery)}&t=&z=${shippingLat && shippingLng ? 16 : 12}&ie=UTF8&iwloc=&output=embed`;
    return (
      <Card
        style={{
          border: '1px solid #ddd',
          borderRadius: '0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          overflow: 'hidden',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            borderBottom: '1px solid #eee',
            backgroundColor: '#fff',
          }}
        >
          <span style={{ fontWeight: '600', fontSize: '16px' }}>
            Delivery Location
          </span>
          <></>
          {/* <Button variant="light" size="sm" style={{ fontSize: "14px", backgroundColor: "#fff", border: "1px solid #F5F8FB", borderRadius: "0", padding: "6px 12px", color: "black" }}>
            <Pin /> &nbsp;&nbsp; Pin Delivery Location
          </Button> */}
        </div>
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '500px',
            padding: '10px',
          }}
        >
          <iframe
            src={mapSrc}
            title="delivery-location"
            style={{ width: '100%', height: '100%', border: 'none' }}
            allowFullScreen
            loading="lazy"
          />
        </div>
      </Card>
    );
  };

  const PopularPicks = () => {
    if (isMobile) {
      return (
        <Card
          style={{
            marginTop: '15px',
            padding: '15px',
            borderRadius: '0',
            backgroundColor: '#F5F8FB',
            border: 'none',
          }}
        >
          <h6 style={{ fontWeight: 'bold', marginBottom: '15px' }}>
            Popular Picks for You
          </h6>
          <div
            style={{
              display: 'flex',
              overflowX: 'auto',
              gap: '15px',
              scrollbarWidth: 'none',
            }}
          >
            <style>{`
              div::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            {popularPicks.map((prod) => (
              <div key={prod.id} style={{ flex: '0 0 auto', width: '200px' }}>
                <Card
                  style={{
                    borderRadius: '0',
                    cursor: 'pointer',
                    border: 'none',
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                    height: '150px',
                    flexWrap: 'nowrap',
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                  }}
                  onClick={() => (window.location.href = `/product/${prod.id}`)}
                >
                  <div
                    style={{
                      flex: '0 0 40%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '10px',
                      backgroundColor: '#fff',
                    }}
                  >
                    <Card.Img
                      src={prod?.image}
                      alt={prod?.name}
                      style={{
                        maxHeight: '95%',
                        maxWidth: '95%',
                        border: '1px solid #ddd',
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      flex: '1',
                      padding: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'start',
                    }}
                  >
                    <div>
                      <Card.Title
                        style={{
                          fontSize: '14px',
                          marginBottom: '5px',
                          color: '#333',
                        }}
                      >
                        {prod?.name}
                      </Card.Title>
                      <Card.Text
                        style={{
                          fontSize: '13px',
                          color: '#777',
                          marginBottom: '10px',
                        }}
                      >
                        {prod?.price}
                      </Card.Text>
                    </div>
                    <Button
                      style={{
                        fontSize: '14px', // increased font size for mobile
                        backgroundColor: '#f0f0f0',
                        color: '#333',
                        borderRadius: '0',
                        padding: '10px 16px', // increased padding for a larger button
                        border: 'none',
                        width: '100%',
                        textTransform: 'uppercase',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.location.href = prod?.url;
                      }}
                    >
                      <ShoppingCart /> &nbsp;&nbsp; Buy Now
                    </Button>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </Card>
      );
    }

    return (
      <Card
        style={{
          marginTop: '15px',
          padding: '15px',
          borderRadius: '0',
          backgroundColor: '#F5F8FB',
          border: 'none',
        }}
      >
        <h6 style={{ fontWeight: 'bold', marginBottom: '15px' }}>
          Popular Picks for You
        </h6>
        <Row>
          {popularPicks.map((prod) => (
            <Col
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={prod.id}
              style={{ marginBottom: '15px' }}
            >
              <Card
                style={{
                  borderRadius: '0',
                  cursor: 'pointer',
                  border: 'none',
                  display: 'flex',
                  flexDirection: 'row',
                  width: '110%',
                  height: '150px',
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                }}
                onClick={() => (window.location.href = `/product/${prod.id}`)}
              >
                <div
                  style={{
                    flex: '0 0 40%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px',
                  }}
                >
                  <Card.Img
                    src={prod?.image}
                    alt={prod?.name}
                    style={{
                      maxHeight: '95%',
                      maxWidth: '95%',
                      border: '1px solid #ddd',
                      objectFit: 'contain',
                    }}
                  />
                </div>
                <div
                  style={{
                    flex: '1',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                  }}
                >
                  <div>
                    <Card.Title
                      style={{
                        fontSize: '14px',
                        marginBottom: '5px',
                        color: '#333',
                      }}
                    >
                      {prod?.name}
                    </Card.Title>
                    <Card.Text
                      style={{
                        fontSize: '13px',
                        color: '#777',
                        marginBottom: '10px',
                      }}
                    >
                      {prod?.price}
                    </Card.Text>
                  </div>
                  <Button
                    style={{
                      fontSize: '12px',
                      backgroundColor: '#f0f0f0',
                      color: '#333',
                      borderRadius: '0',
                      padding: '6px 12px',
                      border: 'none',
                      width: '100%',
                      textTransform: 'uppercase',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.href = prod?.url;
                    }}
                  >
                    <ShoppingCart /> &nbsp;&nbsp; Buy Now
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>
    );
  };

  // Updated custom tab style: transparent background, no border
  const customTabsStyle = `
    .custom-tabs > .nav-tabs {
      border-bottom: 1px solid #ddd;
    }
    .custom-tabs > .nav-tabs .nav-item.show .nav-link,
    .custom-tabs > .nav-tabs .nav-link.active {
      color: #333;
      border: none;
      background-color: transparent;
      font-weight: bold;
    }
    .custom-tabs > .nav-tabs .nav-link {
      color: #999;
      border: none;
      background-color: transparent;
    }
    .custom-tabs > .nav-tabs .nav-link:hover {
      color: #555;
    }
  `;

  return (
    <Container fluid style={{ padding: '15px', backgroundColor: '#F5F8FB' }}>
      <style>{customTabsStyle}</style>
      <Card
        style={{ padding: '15px', border: 'none', backgroundColor: '#F5F8FB' }}
      >
        <OrderHeader />
        <OrderSummary />
        {isMobile ? (
          <Tabs
            defaultActiveKey="order"
            id="mobile-order-tabs"
            className="mb-3 custom-tabs"
          >
            <Tab eventKey="order" title="Order Details">
              <div style={{ padding: '10px' }}>
                <ProductInfo />
                <TrackingHistory />
              </div>
            </Tab>
            <Tab
              eventKey="customer"
              title="Customer Info"
              style={{ backgroundColor: 'none' }}
            >
              <div style={{ padding: '10px' }}>
                <CustomerInfo />
                <DeliveryLocation />
              </div>
            </Tab>
          </Tabs>
        ) : (
          <Row>
            <Col xs={12} md={6} style={{ marginBottom: '15px' }}>
              <CustomerInfo />
              <DeliveryLocation />
            </Col>
            <Col xs={12} md={6} style={{ marginBottom: '15px' }}>
              <ProductInfo />
              <TrackingHistory />
            </Col>
          </Row>
        )}
        {/* <PopularPicks /> */}
      </Card>

      <Modal
        show={showPanModal}
        onHide={() => {
          if (!panSubmitting) setShowPanModal(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 'bold' }}>
            {panNumber ? 'Update PAN Number' : 'Add PAN Number'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePanSubmit}>
          <Modal.Body>
            {panSuccess && (
              <Alert variant="success" className="py-2 mb-3">
                {panSuccess}
              </Alert>
            )}
            {panError && (
              <Alert variant="danger" className="py-2 mb-3">
                {panError}
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '14px', fontWeight: 600 }}>
                Order No.
              </Form.Label>
              <Form.Control
                type="text"
                value={orderNumber || '—'}
                disabled
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label style={{ fontSize: '14px', fontWeight: 600 }}>
                PAN Number <span style={{ color: 'red' }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. ABCDE1234F"
                maxLength={10}
                value={panInput}
                onChange={(e) => {
                  setPanInput(e.target.value.toUpperCase());
                  if (panError) setPanError('');
                }}
                disabled={panSubmitting}
                autoFocus
              />
              <Form.Text className="text-muted" style={{ fontSize: '12px' }}>
                Enter standard 10-character alphanumeric PAN
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowPanModal(false)}
              disabled={panSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={panSubmitting || !panInput.trim()}
            >
              {panSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                'Save PAN'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default OrderTracking;
